import React from 'react'
import { Field, FieldArray, FormSection } from 'redux-form/immutable'
import { Map } from 'immutable'
import cn from 'classnames'

import SectionHeader from 'app/components/SectionHeader'
// @ts-ignore
import ErrorBlock from 'app/components/ErrorBlock'
import Button from 'app/components/ButtonDefault'
// @ts-ignore
import TextArea from 'app/components/TextArea'
import Divider from 'app/components/Divider'
import Graph from 'app/components/SVGS/Graph'
import { REVIEWS_PATH, REVIEWS_SURVEYS } from 'app/constants/navLinks'
import ReviewsSurveyAccessibleReviewerTypeSection from './ReviewsSurveyAccessibleReviewerTypeSection'
import ReviewsSurveyOpenEndedQuestionSection from './ReviewsSurveyOpenEndedQuestionSection'
import ReviewsSurveyCompetenciesSection from './ReviewsSurveyCompetenciesSection'
import { IReviewsSurveyFormProps } from './interface'
import { TYPE_OF_REVIEWERS } from 'app/constants/reviews'
import i18next from 'i18next'
// @ts-ignore
import { maxLength, required } from 'helpers/validators'
import { trimmed } from 'helpers/reviews'
import './styles.scss'

const bc = 'reviews-survey-form'

const isRequired = required()
const maxLength400 = maxLength(400)
const maxLength200 = maxLength(200)
const competencyNameMustBeUniq = (value: string, values: Map<string, any>) => {
  let count = 0
  values.get('competenciesQuestions').forEach((competency: Map<string, any>) => {
    const name = competency.get('name') && competency.get('name').trim()
    if (name === (value && value.trim())) {
      count = count + 1
    }
  })
  if (count > 1) {
    return i18next.t('admin:Competency name must be unique')
  }
  return undefined
}
const validateTextArea = [isRequired, maxLength400]

const validateMin = (typeOfReviewer: TYPE_OF_REVIEWERS) => (value: string, allValues: Map<string, any>) => {
  if (parseInt(value) > parseInt(allValues.get(`${typeOfReviewer}Max`))) {
    return i18next.t('admin:Min must be less then Max', { typeOfReviewer })
  }
  return undefined
}

const validateMax = (typeOfReviewer: TYPE_OF_REVIEWERS) => (value: string, allValues: Map<string, any>) => {
  if (parseInt(value) < parseInt(allValues.get(`${typeOfReviewer}Min`))) {
    return i18next.t('admin:Max must be bigger then Min', { typeOfReviewer })
  }
  return undefined
}

const validateTextAreaForReviewers = (typeOfReviewer: TYPE_OF_REVIEWERS) => (
  value: string,
  allValues: Map<string, any>
) => {
  if (parseInt(allValues.get(`${typeOfReviewer}Max`)) >= 1) {
    return isRequired(value) || maxLength400(value)
  }
  return undefined
}

const vReviewerMinS = validateMin(TYPE_OF_REVIEWERS.SUPERIOR)
const vReviewerMaxS = validateMax(TYPE_OF_REVIEWERS.SUPERIOR)
const vReviewerTextAreaS = validateTextAreaForReviewers(TYPE_OF_REVIEWERS.SUPERIOR)
const vReviewerMinP = validateMin(TYPE_OF_REVIEWERS.PEER)
const vReviewerMaxP = validateMax(TYPE_OF_REVIEWERS.PEER)
const vReviewerTextAreaP = validateTextAreaForReviewers(TYPE_OF_REVIEWERS.PEER)
const vReviewerMinE = validateMin(TYPE_OF_REVIEWERS.EMPLOYEE)
const vReviewerMaxE = validateMax(TYPE_OF_REVIEWERS.EMPLOYEE)
const vReviewerTextAreaE = validateTextAreaForReviewers(TYPE_OF_REVIEWERS.EMPLOYEE)

const ReviewsSurveyForm: React.FC<IReviewsSurveyFormProps | any> = ({
  handleSubmit,
  title,
  publishReviewsSurvey,
  invalid,
  pristine,
  isEditForm,
  error,
  anyTouched,
  isPublished,
  t,
}) => {
  return (
    <div>
      <SectionHeader title={t(`admin:${title}`)} backHref={`${REVIEWS_PATH}${REVIEWS_SURVEYS}`}>
        <Button xs outline onClick={handleSubmit} disabled={invalid || pristine}>
          {t('common:Save')}
        </Button>
        {isEditForm && !isPublished && (
          <Button
            xs
            primary
            type="button"
            disabled={invalid || !pristine}
            onClick={handleSubmit((values: any) => {
              publishReviewsSurvey(values.get('id'))
            })}
          >
            {t('common:Publish')}
          </Button>
        )}
      </SectionHeader>
      <Divider margin="0 0 32px 0" padding="0 8px" />
      {anyTouched && error && <ErrorBlock>{t(`admin:${error}`)}</ErrorBlock>}
      <form onSubmit={handleSubmit}>
        <div className={`${bc}__section`}>
          <div className={`${bc}__row`}>
            <Field
              name="name"
              component={TextArea}
              label={`${t('admin:Survey Name')}:`}
              placeholder={t('admin:Write Survey Name')}
              className={`${bc}__row-item`}
              maxCharacters={400}
              validate={validateTextArea}
              normalize={trimmed}
            />
            <div className={`${bc}__row-item`} />
          </div>
          <div className={`${bc}__row`}>
            <Field
              name="participantWelcomeText"
              component={TextArea}
              classTextArea={`${bc}__textarea`}
              label={`${t('admin:User Welcome Text')}:`}
              className={`${bc}__row-item`}
              maxCharacters={400}
              validate={validateTextArea}
              normalize={trimmed}
            />
            <Field
              name="reviewerWelcomeText"
              component={TextArea}
              classTextArea={`${bc}__textarea`}
              label={`${t('admin:Reviewer Welcome Text')}:`}
              className={`${bc}__row-item`}
              maxCharacters={400}
              validate={validateTextArea}
              normalize={trimmed}
            />
          </div>
          <div className={`${bc}__row`}>
            <Field
              name="participantInfo"
              component={TextArea}
              classTextArea={`${bc}__textarea`}
              label={`${t('admin:User Info')}:`}
              placeholder={t('admin:Please add your contact info correctly')}
              className={`${bc}__row-item`}
              maxCharacters={400}
              validate={validateTextArea}
            />
            <div className={`${bc}__row-item`} />
          </div>
          <div className={`${bc}__row`}>
            <Field
              name="participantInstructions"
              component={TextArea}
              classTextArea={`${bc}__textarea`}
              label={`${t('admin:Self Review Instructions')}:`}
              className={cn(`${bc}__row-item`, `margin-bottom-set-0`)}
              maxCharacters={400}
              validate={validateTextArea}
              normalize={trimmed}
            />
            <Field
              name="reviewerInstructions"
              component={TextArea}
              classTextArea={`${bc}__textarea`}
              label={`${t('admin:Nominated Reviewer Instructions')}:`}
              className={`${bc}__row-item`}
              maxCharacters={400}
              validate={validateTextArea}
              normalize={trimmed}
            />
          </div>
        </div>
        <div className={cn(`${bc}__section`, `${bc}__section__with-border`)}>
          <div className={`${bc}__row`} style={{ height: '433px' }}>
            <div className={cn(`${bc}__row-item`, `${bc}__row-item-center`)}>
              <h3 className={`${bc}__row-item-title`}>{t('admin:Competencies')}:</h3>
              <div className={`${bc}__row-item-subtitle`}>
                {t(
                  'admin:Competencies are the larger “categories” which are made up by averaging a number of questions'
                )}
                .
                <br />
                {t('admin:The report requires a minimum of 3, and a maximum of 10 competencies')}
              </div>
            </div>
            <div className={cn(`${bc}__row-item`, `${bc}__row-item-center`)}>
              <Graph />
              <div className={`${bc}__square-legend`}>{t('admin:Competencies look like this in the radial graph')}</div>
            </div>
          </div>
          <Divider margin="0 0 32px 0" />
          <FieldArray
            name="competenciesQuestions"
            // @ts-ignore
            component={ReviewsSurveyCompetenciesSection}
            validateTextArea={[isRequired, maxLength200]}
            validateCompetencyTextArea={[isRequired, maxLength200, competencyNameMustBeUniq]}
            isPublished={isPublished}
          />
        </div>
        <div className={cn(`${bc}__section`, `${bc}__section__with-border`)}>
          <div className={`${bc}__row`} style={{ height: '433px' }}>
            <div className={cn(`${bc}__row-item`, `${bc}__row-item-center`)}>
              <h3 className={`${bc}__row-item-title`}>{`${t('admin:Open ended Question')}:`}</h3>
              <div className={`${bc}__row-item-subtitle`}>
                {t(
                  'admin:Open ended questions allow users and reviewers to reflect on specific questions in their own words'
                )}
              </div>
            </div>
            <div className={cn(`${bc}__row-item`, `${bc}__row-item-center`)}>
              <div className={`${bc}__square-row`}>
                <div className={`${bc}__square`} style={{ backgroundColor: '#664ea7' }}>
                  <div className={`${bc}__square-title`}>{`${t('admin:Self')}:`}</div>
                  <div className={`${bc}__square-text`}>
                    {t('admin:This will be their own response to this question')}
                  </div>
                </div>
                <div className={`${bc}__square`} style={{ backgroundColor: '#2dd878' }}>
                  <div className={`${bc}__square-title`}>{`${t('admin:Superior')}:`}</div>
                  <div className={`${bc}__square-text`}>
                    {t('admin:This will be their superior’s response to this question')}
                  </div>
                </div>
              </div>
              <div className={`${bc}__square-row`}>
                <div className={`${bc}__square`} style={{ backgroundColor: '#3c78d8' }}>
                  <div className={`${bc}__square-title`}>{`${t('admin:Peers')}:`}</div>
                  <div className={`${bc}__square-text`}>
                    {t('admin:This will be peer response', { number: 1 })}
                    <br />
                    {t('admin:This will be peer response', { number: 2 })}
                    <br />
                    {t('admin:This will be peer response', { number: 3 })}
                    <br />
                    {t('admin:This will be peer response', { number: 4 })}
                  </div>
                </div>
                <div className={`${bc}__square`} style={{ backgroundColor: '#f1c331' }}>
                  <div className={`${bc}__square-title`}>{`${t('admin:Direct Reports')}:`}</div>
                  <div className={`${bc}__square-text`}>
                    {t('admin:This will be DR response', { number: 1 })}
                    <br />
                    {t('admin:This will be DR response', { number: 2 })}
                    <br />
                    {t('admin:This will be DR response', { number: 3 })}
                    <br />
                    {t('admin:This will be DR response', { number: 4 })}
                    <br />
                    {t('admin:This will be DR response', { number: 5 })}
                    <br />
                    {t('admin:This will be DR response', { number: 6 })}
                  </div>
                </div>
              </div>
              <div className={`${bc}__square-legend`}>
                {t('admin:Open ended response look like this in the report')}
              </div>
            </div>
          </div>
          <Divider margin="0 0 32px 0" />
          <FormSection name="openEndedQuestions">
            <div className={`${bc}__row`}>
              <div className={`${bc}__row-item`}>
                <h3 className={`${bc}__row-item-title`}>{t('admin:Time', { time: 1 })}</h3>
                <FieldArray
                  name="t1"
                  // @ts-ignore
                  component={ReviewsSurveyOpenEndedQuestionSection}
                  min={0}
                  validateTextArea={[isRequired, maxLength200]}
                  isPublished={isPublished}
                />
              </div>
              <div className={`${bc}__row-item`}>
                <h3 className={`${bc}__row-item-title`}>{t('admin:Time', { time: 2 })}</h3>
                <FieldArray
                  name="t2"
                  // @ts-ignore
                  component={ReviewsSurveyOpenEndedQuestionSection}
                  min={0}
                  validateTextArea={[isRequired, maxLength200]}
                  isPublished={isPublished}
                />
              </div>
            </div>
          </FormSection>
        </div>
        <div className={`${bc}__section`}>
          <div className={`${bc}__row`}>
            <Field
              name="nominationInstructions"
              component={TextArea}
              classTextArea={`${bc}__textarea`}
              label={`${t('admin:Nomination Instructions')}:`}
              className={`${bc}__row-item`}
              maxCharacters={400}
              validate={validateTextArea}
              normalize={trimmed}
            />
            <div className={`${bc}__row-item`} />
          </div>
          <ReviewsSurveyAccessibleReviewerTypeSection
            typeOfReviewer={TYPE_OF_REVIEWERS.SUPERIOR}
            validateTextArea={vReviewerTextAreaS}
            validateReviewerMin={vReviewerMinS}
            validateReviewerMax={vReviewerMaxS}
            isPublished={isPublished}
          />
          <ReviewsSurveyAccessibleReviewerTypeSection
            typeOfReviewer={TYPE_OF_REVIEWERS.PEER}
            validateTextArea={vReviewerTextAreaP}
            validateReviewerMin={vReviewerMinP}
            validateReviewerMax={vReviewerMaxP}
            isPublished={isPublished}
          />
          <ReviewsSurveyAccessibleReviewerTypeSection
            typeOfReviewer={TYPE_OF_REVIEWERS.EMPLOYEE}
            validateTextArea={vReviewerTextAreaE}
            validateReviewerMin={vReviewerMinE}
            validateReviewerMax={vReviewerMaxE}
            isPublished={isPublished}
          />
          <div className={`${bc}__row`}>
            <Field
              name="participantEndText"
              component={TextArea}
              classTextArea={`${bc}__textarea`}
              label={`${t('admin:Add Thank You text User')}:`}
              className={`${bc}__row-item`}
              maxCharacters={400}
              validate={validateTextArea}
              normalize={trimmed}
            />
            <Field
              name="reviewerEndText"
              component={TextArea}
              classTextArea={`${bc}__textarea`}
              label={`${t('admin:Add Thank You text Reviewer')}:`}
              className={`${bc}__row-item`}
              maxCharacters={400}
              validate={validateTextArea}
              normalize={trimmed}
            />
          </div>
        </div>
      </form>
    </div>
  )
}

export default ReviewsSurveyForm
